var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import StylePropType from 'react-style-proptype';
import { cx } from 'emotion';
import TabGroup from './TabGroup';
import Tab from './Tab';
const generateTabMap = tabs => tabs.reduce((tabAcc, tab) => (Object.assign(Object.assign({}, tabAcc), { [tab.id]: tab })), {});
const validateIcons = tabs => {
    const tabsWithIcons = tabs.filter(tab => Boolean(tab.icon));
    const isValid = !tabsWithIcons.length || tabsWithIcons.length === tabs.length;
    if (!isValid) {
        console.warn('TabMenu: All tabs must have icons for them to render. Ignoring icons for all tabs...');
    }
    return isValid;
};
const validateDescriptions = tabs => {
    const tabsWithDescriptions = tabs.filter(tab => Boolean(tab.description));
    const isValid = !tabsWithDescriptions.length || tabsWithDescriptions.length === tabs.length;
    if (!isValid) {
        console.warn('TabMenu: All tabs must have descriptions for them to render. Ignoring descriptions for all tabs...');
    }
    return isValid;
};
const TabMenu = (_a) => {
    var { activeTabId, tabs, tabGroups, onTabClick, bodyStyle, tabStyle, className, style } = _a, rest = __rest(_a, ["activeTabId", "tabs", "tabGroups", "onTabClick", "bodyStyle", "tabStyle", "className", "style"]);
    const [tabMap, setTabMap] = useState(generateTabMap(tabs));
    const [showIcons, setShowIcons] = useState(validateIcons(tabs));
    const [showDescriptions, setShowDescriptions] = useState(validateDescriptions(tabs));
    useEffect(() => {
        // Create a lookup mapping of tabs by id to optimize
        setTabMap(generateTabMap(tabs));
        setShowIcons(validateIcons(tabs));
        setShowDescriptions(validateDescriptions(tabs));
    }, [tabs]);
    if (!tabs || !tabs.length) {
        return null;
    }
    let activeTab = tabs[0];
    if (activeTabId) {
        activeTab = tabMap[activeTabId] || activeTab;
    }
    return (React.createElement("div", Object.assign({ className: cx('tab-menu', className), style: style }, rest),
        React.createElement("div", { className: "tabs", style: tabStyle }, tabGroups.length > 1
            ? tabGroups.map(({ label, tabIds = [] }) => (React.createElement(TabGroup, { key: `${label}-${tabIds.join('-')}`, label: label }, tabIds.map(tabId => {
                const { id, title, description, icon } = tabMap[tabId];
                return (React.createElement(Tab, Object.assign({ key: id, id: id, title: title, onTabClick: onTabClick, isActive: activeTab.id === id }, (showIcons ? { icon } : {}), (showDescriptions ? { description } : {}))));
            }))))
            : tabs.map(({ id, title, description, icon }) => (React.createElement(Tab, Object.assign({ key: id, id: id, title: title, onTabClick: onTabClick, isActive: activeTab.id === id }, (showIcons ? { icon } : {}), (showDescriptions ? { description } : {})))))),
        React.createElement("div", { className: "active-tab-body", style: bodyStyle }, activeTab && activeTab.body)));
};
const tabShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    icon: PropTypes.func,
    body: PropTypes.node,
});
const tabGroupShape = PropTypes.shape({
    label: PropTypes.string.isRequired,
    tabIds: PropTypes.arrayOf(PropTypes.string).isRequired,
});
TabMenu.propTypes = {
    className: PropTypes.string,
    activeTabId: PropTypes.string,
    tabs: PropTypes.arrayOf(tabShape).isRequired,
    tabGroups: PropTypes.arrayOf(tabGroupShape),
    onTabClick: PropTypes.func,
    bodyStyle: StylePropType,
    tabStyle: StylePropType,
    style: StylePropType,
};
TabMenu.defaultProps = {
    className: '',
    onTabClick: () => { },
    tabGroups: [],
    bodyStyle: {},
    tabStyle: {},
    style: {},
};
export default TabMenu;
